import { useEffect, useState } from "react";
import { createDatafeed } from "../datafeed/datafeed";
import ChartEngine from "../engine/ChartEngine";
import { drawingsStorage } from "../settings/savedDrawings";

/**
 * Creates the chart engine inside `containerRef` with its own datafeed
 * instance, passes the symbol, resolution, chart type and time zone to it
 * and destroys it on unmount. History, realtime bars and switches are handled by the engine.
 *
 * @param {object} params
 * @param {React.RefObject<HTMLElement>} params.containerRef
 * @param {string} params.symbol      - pair slug, e.g. "BTCUSDT"
 * @param {string} params.resolution  - TV resolution from the chart settings
 * @param {number} params.chartType   - CHART_TYPE_* from the chart settings
 * @param {string} params.timezone    - IANA zone of the time axis
 * @param {string|null} params.locale - language of month names on the time axis
 * @returns {ChartEngine|null} null until the engine is created
 */
const useChartEngine = ({ containerRef, symbol, resolution, chartType, timezone, locale }) => {
  const [engine, setEngine] = useState(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return undefined;

    const instance = new ChartEngine(container, { datafeed: createDatafeed(), drawingsStorage });

    setEngine(instance);

    return () => {
      instance.destroy();
      setEngine(null);
    };
  }, [containerRef]);

  useEffect(() => {
    engine?.setTimezone(timezone, locale);
  }, [engine, timezone, locale]);

  // A new pair or timeframe unsubscribes the old stream and loads the new history
  useEffect(() => {
    if (engine && symbol) engine.setSymbol(symbol);
  }, [engine, symbol]);

  useEffect(() => {
    engine?.setResolution(resolution);
  }, [engine, resolution]);

  useEffect(() => {
    engine?.setChartType(chartType);
  }, [engine, chartType]);

  return engine;
};

export default useChartEngine;
